// Shared Excel + PDF export helpers for the MIS report tabs.
// Excel  -> xlsx + file-saver (same stack the Finance module uses).
// PDF    -> jsPDF with a small built-in table renderer (jspdf-autotable isn't
//           installed), handling column widths, header shading and page breaks.
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import * as Cookies from "es-cookie";

// Institute header (name / address / logo) for report documents — same source
// the Finance module uses: cookies for the text, and the logo fetched from the
// rizee files host and converted to a data URL so jsPDF can embed it.
async function getOrgHeader() {
  const name = Cookies.get("institutename") || "";
  const address = Cookies.get("institute_address") || "";
  const logoFile = Cookies.get("institute_logo") || "";
  let logo = "";
  if (logoFile) {
    try {
      const res = await fetch(`https://support.rizee.in/files/${logoFile}`, { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        logo = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve("");
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) { /* logo is optional — ignore fetch/CORS errors */ }
  }
  return { name, address, logo };
}

// sheets: [{ name, aoa: [[...headerCells], ...rows], cols?: [{wch}] }]
export function exportExcel(sheets, filename) {
  const wb = XLSX.utils.book_new();
  (sheets || []).forEach((s, i) => {
    const ws = XLSX.utils.aoa_to_sheet(s.aoa || [[]]);
    if (s.cols) ws["!cols"] = s.cols;
    // Sheet names are capped at 31 chars and can't contain []:*?/\
    const name = (s.name || `Sheet ${i + 1}`).replace(/[[\]:*?/\\]/g, " ").slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), filename);
}

// tables: [{ heading, columns: [..], rows: [[..], ..] }]
export async function exportPdf(title, tables, filename, subtitle) {
  const org = await getOrgHeader();
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 32;
  const avail = pageW - margin * 2;
  let y = margin + 4;

  // Institute header: logo (left) + name + address.
  let textX = margin;
  const headTop = y;
  if (org.logo) {
    try {
      const props = doc.getImageProperties(org.logo);
      const h = 44;
      const w = props.width && props.height ? Math.min(150, props.width * (h / props.height)) : 44;
      doc.addImage(org.logo, props.fileType || "PNG", margin, headTop, w, h);
      textX = margin + w + 14;
    } catch (e) { /* bad image — skip logo */ }
  }
  if (org.name) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(15, 23, 42);
    doc.text(String(org.name), textX, headTop + 14);
  }
  if (org.address) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    const addrLines = doc.splitTextToSize(String(org.address), pageW - textX - margin);
    doc.text(addrLines, textX, headTop + 30);
  }
  y = headTop + 52;
  doc.setDrawColor(203, 213, 225);
  doc.line(margin, y, pageW - margin, y);
  y += 18;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text(String(title || "Report"), margin, y);
  y += 18;
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(String(subtitle), margin, y);
    y += 14;
  }

  const ensure = (need) => { if (y + need > pageH - margin) { doc.addPage(); y = margin + 4; } };

  (tables || []).forEach((t) => {
    const cols = t.columns || [];
    if (!cols.length) return;
    const colW = avail / cols.length;

    ensure(46);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(String(t.heading || ""), margin, y);
    y += 12;

    // header band
    doc.setFillColor(241, 245, 249);
    doc.rect(margin, y - 2, avail, 16, "F");
    doc.setFontSize(8.5);
    doc.setTextColor(51, 65, 85);
    cols.forEach((c, i) => doc.text(String(c), margin + i * colW + 4, y + 9, { maxWidth: colW - 6 }));
    y += 18;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(51, 65, 85);
    (t.rows || []).forEach((row) => {
      ensure(16);
      cols.forEach((_, i) => {
        const cell = row[i];
        doc.text(String(cell == null ? "" : cell), margin + i * colW + 4, y + 8, { maxWidth: colW - 6 });
      });
      y += 15;
      doc.setDrawColor(238, 242, 247);
      doc.line(margin, y - 4, margin + avail, y - 4);
    });
    y += 18;
  });

  doc.save(filename);
}
